'use strict'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {setSessionJSONItem} from '@salesforce/retail-react-app/app/utils/utils'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../utils/site-config'

/**
 * Creates and returns a configured UserManager instance for social login using OIDC.
 * Dynamically imports oidc-client-ts to reduce initial bundle size (~50-80KB savings).
 *
 * @param {string} provider - The social login provider (e.g., 'google', 'facebook').
 * @param {string} brandId - The brand identifier (e.g., 'GRG', 'DYN').
 * @returns {Promise<UserManager>} A UserManager instance configured with OIDC settings for the specified provider and brand.
 */
const getSocialLoginManager = async (provider, brandId) => {
    setSessionJSONItem(
        'socialLoginData',
        JSON.stringify({
            provider,
            brandId
        })
    )

    // Lazy load oidc-client-ts - only downloaded when user initiates social login
    const {UserManager} = await import('oidc-client-ts')

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const sfidClientID = getSiteEnvVarValue(config.app, 'SFID_CLIENT_ID', siteId)
    const baseUrl = getSiteEnvVarValue(config.app, 'SFID_SOCIAL_LOGIN_BASE_URL', siteId)
    const brandName = brandId === 'GRG' ? 'Garage' : 'Dynamite'
    const providerName = provider.charAt(0).toUpperCase() + provider.slice(1)
    const oidcConfig = {
        authority: baseUrl,
        client_id: sfidClientID,
        redirect_uri: `${window.location.origin}/sso-callback`,
        response_type: 'code',
        scope: 'openid',
        extraQueryParams: {
            sso_provider: `${brandName}_${providerName}`,
            device: 'Web',
            app: brandId
        }
    }

    const userManager = new UserManager({
        ...oidcConfig
    })

    return userManager
}

export {getSocialLoginManager}
